import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Button } from '../../../components/ui/Button';
import { KeyValueEditor, type KeyValueRow } from '../../../components/ui/KeyValueEditor';
import { createPlan, type CreatePlanRequest } from '../../../modules/billing/api/billing.client';
import { ToggleRight, ToggleLeft } from 'lucide-react';
import { showErrorToast } from '../../../utils/errorHandler';

interface CreatePlanFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface FormData {
  name: string;
  interval: 'month' | 'year';
  amount: string;
  currency: string;
  description: string;
  marketing_features: string;
  statement_descriptor: string;
  analytics: boolean;
  priority_support: boolean;
  sso: boolean;
  audit_logs: boolean;
  custom_branding: boolean;
  max_students: string;
  max_teachers: string;
  max_schools: string;
}

/** Convert key-value rows to JSON object; empty keys skipped; numeric strings parsed as numbers */
function rowsToObject(rows: KeyValueRow[]): Record<string, unknown> {
  const obj: Record<string, unknown> = {};
  for (const { key, value } of rows) {
    const k = key.trim();
    if (!k) continue;
    const v = value.trim();
    if (v === '') {
      obj[k] = '';
      continue;
    }
    const num = Number(v);
    obj[k] = v !== '' && !Number.isNaN(num) && String(num) === v ? num : v;
  }
  return obj;
}

const defaultAttributes: KeyValueRow[] = [
  { key: 'tier', value: 'pro' },
  { key: 'ai_minutes', value: '500' },
];
const defaultMetadata: KeyValueRow[] = [
  { key: 'internal_id', value: 'pro-v1' },
  { key: 'segment', value: 'b2b' },
];

export const CreatePlanForm: React.FC<CreatePlanFormProps> = ({ onSuccess, onCancel }) => {
  const [attributesRows, setAttributesRows] = useState<KeyValueRow[]>(defaultAttributes);
  const [metadataRows, setMetadataRows] = useState<KeyValueRow[]>(defaultMetadata);
  const [attributesEnabled, setAttributesEnabled] = useState(false);
  const [metadataEnabled, setMetadataEnabled] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      interval: 'month',
      currency: 'usd',
      description: '',
      marketing_features: '',
      statement_descriptor: '',
      analytics: true,
      priority_support: false,
      sso: false,
      audit_logs: false,
      custom_branding: false,
      max_students: '20',
      max_teachers: '5',
      max_schools: '',
    },
  });

  const onSubmit = async (values: FormData) => {
    const attributes = attributesEnabled ? rowsToObject(attributesRows) : {};
    const metadata = metadataEnabled ? rowsToObject(metadataRows) : {};

    const marketingFeatures = values.marketing_features
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);

    const limits: Record<string, number> = {};
    const maxStudents = parseInt(values.max_students, 10);
    const maxTeachers = parseInt(values.max_teachers, 10);
    if (!Number.isNaN(maxStudents) && maxStudents >= 0) limits.max_students = maxStudents;
    if (!Number.isNaN(maxTeachers) && maxTeachers >= 0) limits.max_teachers = maxTeachers;
    if (values.max_schools.trim()) {
      const maxSchools = parseInt(values.max_schools, 10);
      if (!Number.isNaN(maxSchools) && maxSchools >= 0) limits.max_schools = maxSchools;
    }

    const payload: CreatePlanRequest = {
      name: values.name,
      interval: values.interval,
      amount: parseInt(values.amount, 10) || 0,
      currency: values.currency,
      description: values.description.trim() || undefined,
      marketing_features: marketingFeatures.length > 0 ? marketingFeatures : undefined,
      attributes: attributesEnabled && Object.keys(attributes).length > 0 ? attributes : undefined,
      statement_descriptor: values.statement_descriptor.trim() || undefined,
      metadata: metadataEnabled && Object.keys(metadata).length > 0 ? metadata : undefined,
      features: {
        analytics: values.analytics,
        priority_support: values.priority_support,
        sso: values.sso,
        audit_logs: values.audit_logs,
        custom_branding: values.custom_branding,
      },
      limits: Object.keys(limits).length > 0 ? limits : undefined,
    };

    try {
      await createPlan(payload);
      onSuccess?.();
    } catch (err) {
      showErrorToast(err);
    }
  };

  return (
    <form id="create-plan-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4 min-w-0">
      <Input
        label="Plan name"
        {...register('name', { required: 'Required' })}
        error={errors.name?.message}
        placeholder="e.g. School Pro Monthly"
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-app-text dark:text-gray-300 mb-1">Interval</label>
          <select
            {...register('interval', { required: true })}
            className="w-full rounded-lg border border-app-border dark:border-gray-600 bg-app-surface dark:bg-gray-800 px-3 py-2 text-app-text dark:text-gray-100"
          >
            <option value="month">Month</option>
            <option value="year">Year</option>
          </select>
        </div>
        <Input
          label="Currency"
          {...register('currency', { required: 'Required' })}
          error={errors.currency?.message}
          placeholder="e.g. usd"
        />
      </div>
      <Input
        label="Amount (cents)"
        type="number"
        min={0}
        {...register('amount', { required: 'Required', min: { value: 0, message: 'Must be ≥ 0' } })}
        error={errors.amount?.message}
        placeholder="e.g. 1999 or 19900"
      />
      <Textarea
        label="Description"
        {...register('description')}
        rows={2}
        placeholder="e.g. Best for growing schools. Includes AI minutes, advanced analytics, and priority support."
      />
      <Textarea
        label="Marketing features (one per line)"
        {...register('marketing_features')}
        rows={4}
        placeholder={'500 AI minutes/month\nAdvanced analytics dashboards\nPriority support'}
        helperText="One feature per line"
      />
      <Input
        label="Statement descriptor"
        {...register('statement_descriptor')}
        placeholder="e.g. SCHOOL PRO (max 22 chars for Stripe)"
      />
      <div className="min-w-0 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium text-app-text dark:text-gray-300">Attributes (optional)</span>
          <button
            type="button"
            onClick={() => setAttributesEnabled((prev) => !prev)}
            className={attributesEnabled
              ? 'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50'
              : 'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-app-text-muted dark:text-gray-400 hover:text-app-text dark:hover:text-gray-300 hover:bg-app-surface-muted dark:hover:bg-gray-700'
            }
            aria-pressed={attributesEnabled}
            aria-label={attributesEnabled ? 'Disable attributes' : 'Enable attributes'}
          >
            {attributesEnabled ? (
              <>
                <ToggleLeft className="w-4 h-4" aria-hidden />
                Disable
              </>
            ) : (
              <>
                <ToggleRight className="w-4 h-4" aria-hidden />
                Enable
              </>
            )}
          </button>
        </div>
        {attributesEnabled && (
          <KeyValueEditor
            label="Attributes"
            rows={attributesRows}
            onChange={setAttributesRows}
            namePlaceholder="e.g. tier, ai_minutes"
            valuePlaceholder="e.g. pro, 500"
            nameHelperText="Key name (e.g. tier, ai_minutes)"
            valueHelperText="Value; numbers are sent as numbers"
          />
        )}
      </div>
      <div className="min-w-0 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium text-app-text dark:text-gray-300">Metadata (optional)</span>
          <button
            type="button"
            onClick={() => setMetadataEnabled((prev) => !prev)}
            className={metadataEnabled
              ? 'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50'
              : 'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-app-text-muted dark:text-gray-400 hover:text-app-text dark:hover:text-gray-300 hover:bg-app-surface-muted dark:hover:bg-gray-700'
            }
            aria-pressed={metadataEnabled}
            aria-label={metadataEnabled ? 'Disable metadata' : 'Enable metadata'}
          >
            {metadataEnabled ? (
              <>
                <ToggleLeft className="w-4 h-4" aria-hidden />
                Disable
              </>
            ) : (
              <>
                <ToggleRight className="w-4 h-4" aria-hidden />
                Enable
              </>
            )}
          </button>
        </div>
        {metadataEnabled && (
          <KeyValueEditor
            label="Metadata"
            rows={metadataRows}
            onChange={setMetadataRows}
            namePlaceholder="e.g. internal_id, segment"
            valuePlaceholder="e.g. pro-v1, b2b"
            nameHelperText="Key name (e.g. internal_id, contract)"
            valueHelperText="e.g. pro-v1, annual"
          />
        )}
      </div>

      <fieldset className="space-y-2 min-w-0">
        <legend className="text-sm font-medium text-app-text dark:text-gray-300">Features (checkboxes)</legend>
        <div className="flex flex-wrap gap-3 sm:gap-4">
          {[
            { name: 'analytics', label: 'Analytics' },
            { name: 'priority_support', label: 'Priority support' },
            { name: 'sso', label: 'SSO' },
            { name: 'audit_logs', label: 'Audit logs' },
            { name: 'custom_branding', label: 'Custom branding' },
          ].map(({ name, label }) => (
            <div key={name} className="flex items-center gap-2">
              <input
                type="checkbox"
                id={name}
                {...register(name as keyof FormData)}
                className="rounded border-app-border dark:border-gray-600 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor={name} className="text-sm text-app-text dark:text-gray-300">
                {label}
              </label>
            </div>
          ))}
        </div>
      </fieldset>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Input
          label="Max students"
          type="number"
          min={0}
          {...register('max_students', { required: 'Required', min: { value: 0, message: 'Must be ≥ 0' } })}
          error={errors.max_students?.message}
          placeholder="e.g. 50"
        />
        <Input
          label="Max teachers"
          type="number"
          min={0}
          {...register('max_teachers', { min: { value: 0, message: 'Must be ≥ 0' } })}
          error={errors.max_teachers?.message}
          placeholder="e.g. 10"
        />
        <Input
          label="Max schools (optional)"
          type="number"
          min={0}
          {...register('max_schools', { min: { value: 0, message: 'Must be ≥ 0' } })}
          error={errors.max_schools?.message}
          placeholder="e.g. 5 or leave empty"
        />
      </div>

      <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t border-app-border dark:border-gray-700">
        <Button type="button" variant="outline" onClick={onCancel} className="w-full sm:w-auto">
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
          {isSubmitting ? 'Creating…' : 'Create plan'}
        </Button>
      </div>
    </form>
  );
};
